// Soft, branded accordion built on Radix. Used by the FAQ page (and any
// future docs surface) — collapsible group with chevron rotation + content
// slide-down via Radix's data-state attributes.

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "../cn.ts";

export const Accordion = AccordionPrimitive.Root;

export const AccordionItem = forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(function AccordionItem({ className, ...rest }, ref) {
  return (
    <AccordionPrimitive.Item
      ref={ref}
      className={cn(
        "overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-paper)] transition-colors",
        "data-[state=open]:border-[var(--brand-pink)] data-[state=open]:bg-white",
        className,
      )}
      {...rest}
    />
  );
});

export const AccordionTrigger = forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(function AccordionTrigger({ className, children, ...rest }, ref) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          "group flex flex-1 items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold text-[var(--brand-ink)] transition-all",
          "hover:bg-[var(--brand-pink-soft)]/60",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose focus-visible:ring-offset-2",
          "[&[data-state=open]>svg]:rotate-180",
          className,
        )}
        {...rest}
      >
        {children}
        <ChevronDown
          className="h-4 w-4 shrink-0 text-[var(--brand-rose)] transition-transform duration-200"
          aria-hidden
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});

export const AccordionContent = forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(function AccordionContent({ className, children, ...rest }, ref) {
  return (
    <AccordionPrimitive.Content
      ref={ref}
      // Radix exposes --radix-accordion-content-height for animating the
      // expanding/collapsing region. Using grid-rows for the smooth transition
      // is also valid, but matching Radix's documented animation hook keeps
      // the API closer to the shadcn convention.
      className={cn(
        "overflow-hidden text-sm leading-6 text-[var(--brand-ink)]/80",
        "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
        className,
      )}
      {...rest}
    >
      <div className="px-5 pb-5 pt-0">{children}</div>
    </AccordionPrimitive.Content>
  );
});
